import React, { ReactNode } from "react";
import { LocalKeys, useTranslation } from "utilities/i18n";
import { Layout } from "views/Main/Layouts";
import { useAccountQuery } from "../View/AccountDetails";
import { Route, Link, Switch } from "polyrouter";
import { InviteMemberModal } from "routes/Admin/Accounts/Members/InviteMemberModal";
import { HelpLink, useFeatureFlagWithTenant } from "elements";
import { OSKeys, isOS } from "utilities/osDetection";
import { useAvailableProductSoftwareByPidQuery } from "generated/graphql";
import { LENS_DESKTOP_MAC, DOCS, LENS_DESKTOP_WINDOWS } from "Constants";

const DesktopSettings: React.FC<{ id: string }> = ({ id }) => {
  const { t } = useTranslation();
  const { tenant } = useAccountQuery(id);

  const { data: downloads, fetching } = useAvailableProductSoftwareByPidQuery({
    variables: {
      pidWindows: LENS_DESKTOP_WINDOWS,
      pidMac: LENS_DESKTOP_MAC
    }
  });

  const isMac = isOS("mac");
  const isWin = isOS("win");

  const links: Array<{ for: OSKeys; version: string; el: ReactNode }> = [
    {
      for: "win",
      version: downloads?.windows?.productBuild?.version || "",
      el: (
        <DisablableButtonLink
          className={isWin ? "btn-primary" : ""}
          href={downloads?.windows?.productBuild?.archiveUrl}
          disabled={fetching}
          anchorKey="DOWNLOAD_FOR_WIN"
        />
      )
    },
    {
      for: "mac",
      version: downloads?.mac?.productBuild?.version || "",
      el: (
        <DisablableButtonLink
          className={isMac ? "btn-primary" : ""}
          href={downloads?.mac?.productBuild?.archiveUrl}
          disabled={fetching}
          anchorKey="DOWNLOAD_FOR_MAC"
        />
      )
    }
  ];

  links.sort((a, b) => {
    return isOS(a.for) ? -1 : 1;
  });

  const [isIntegrationsEnabled] = useFeatureFlagWithTenant(
    "INTEGRATION_AZURE_AD"
  );

  const { Content } = Layout;

  return (
    <Content className="provisioning-settings-content">
      <Content.ScrollingList>
        <div className="card title-card">{t("POLY_LENS_DESKTOP")}</div>
        <div className="mt-15 mb-1 card-title-indent font-bold">
          {t("DESKTOP_INVITE_SUBTITLE")}
        </div>
        <div className="ph-2">
          <p>{t("DESKTOP_INVITE_INFO")}</p>
        </div>
        <div className="mt-15 mb-2 ph-2">
          <Link
            nest
            to={{ pathname: "/invite", search: "device-user=true" }}
            data-testid="desktop-invite"
            className={`btn btn-primary`}
          >
            {t("SEND_EMAIL_INVITE")}
          </Link>
        </div>
        {isIntegrationsEnabled && (
          <>
            <div className="mt-15 mb-1 card-title-indent font-bold">
              {t("DESKTOP_INTEGRATIONS_SUBTITLE")}
            </div>
            <div className="ph-2">
              <p>
                {t("DESKTOP_INTEGRATIONS_INFO")}
                <Link to={{ pathname: "integrations" }}>
                  {t("INTEGRATIONS") + "."}
                </Link>
              </p>
            </div>
          </>
        )}
        <div className="mt-15 mb-1 card-title-indent font-bold">
          {t("DOWNLOAD")}
        </div>
        <div className="ph-2">
          <p>{t("DOWNLOAD_LENS_DESKTOP_INFO")}</p>
          {links.map(link => (
            <div key={link.for} className="d-inline-block mr-1 text-center">
              {link.el}
              {link.version && (
                <p className="font-too-small mt-5">
                  <HelpLink location={DOCS.LENS_DESKTOP_RELEASE_NOTES}>
                    {t("VERSION_NUMBER", { version: link.version })}
                  </HelpLink>
                </p>
              )}
            </div>
          ))}
        </div>
      </Content.ScrollingList>
      <Switch>
        <Route
          nest
          path="/invite"
          render={() => <InviteMemberModal tenant={tenant} />}
        />
      </Switch>
    </Content>
  );
};

export const DisablableButtonLink: React.FC<{
  href: string | null | undefined;
  disabled: boolean;
  className?: string;
  anchorKey: LocalKeys;
}> = ({ href, disabled, className, anchorKey }) => {
  const { t } = useTranslation();

  const classes = ["btn", className].filter(c => !!c).join(" ");

  if (disabled || !href) {
    return <span className={`disabled ${classes}`}>{t(anchorKey)}</span>;
  }

  return (
    <a href={href} className={classes}>
      {t(anchorKey)}
    </a>
  );
};

export default DesktopSettings;
